import type { PaginatedResult } from 'Src/models/pageOpts';
import { QueryOpts } from 'Src/models/queryOpts';
import { coreService } from 'Src/network/AutomatedService';
import { definitions } from 'Src/vendor/posapi/pos';

/**
 * @description A customer identified as associated with the Organization this POS Terminal is under.
 */
type OrgCustomer = definitions['customer.OrgCustomer'];
/**
 * @description Loyalty-related details for a customer within the context of the Loyalty Program (if any)
 * this POS Terminal's Location is enrolled in.
 */
type CustomerDetails = definitions['customer.CustomerDetails'];

/**
 * @description Shape of input when searching for customers.
 */
type CustomerSearch = QueryOpts<definitions['loyalty.OrgCustomerQuery']>;

/**
 * @description Shape of customer search response data.
 */
type CustomerSearchResult = PaginatedResult<OrgCustomer>;

/**
 * @description Shape of a signup when using the UAT Customer Enrollment API.
 */
type UATCustomerEnrollment = definitions['customer.CustomerEnrollment'];

/**
 * @description This lets us know if our Location is enrolled in a Loyalty Program or not.
 */
type LocationProgramStatus = definitions['loyalty.ProgramStatus'];

const service = {
  /**
   * @description Searches for all customers associated with this POS Terminal's Org.
   * Even if this POS Terminal's Location is not participating in Loyalty, it can see customers of the Org.
   * @returns {PaginatedResult<OrgCustomer>}
   */
  search: coreService['POS:GET:Customers'],
  /**
   * @description Get details for a specific customer, including their available points/rewards to redeem
   * within the context of the Loyalty Program (if any) this POS Terminal's Location is enrolled in.
   * @returns {CustomerDetails}
   */
  getDetails: coreService['POS:GET:Customer.Details'],
  /**
   * @description Retrieves status for this POS Terminal's Location so we know if it is enrolled in
   * a Loyalty Program or not.
   */
  getLocationProgramEnrollment: () => coreService['POS:GET:Loyalty.ProgramStatus']({}),

  /**
   * @description Functionality that is only to be used by dev tools.
   * DO NOT USE ANYWHERE ELSE.
   */
  devTools: {
    enrollCustomer: coreService['POS:POST:CustomerEnrollment.UAT'],
  }
};

export default service;

export type { CustomerDetails, CustomerSearch, CustomerSearchResult, LocationProgramStatus, OrgCustomer, UATCustomerEnrollment };
